import { yupResolver } from '@hookform/resolvers/yup';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import Edit from '@mui/icons-material/Edit';
import Route from '@mui/icons-material/Route';
import Schedule from '@mui/icons-material/Schedule';
import StickyNote2 from '@mui/icons-material/StickyNote2';
import Summarize from '@mui/icons-material/Summarize';
import Box from '@mui/material/Box';
import { buttonClasses } from '@mui/material/Button';
import Drawer, { drawerClasses } from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import { Theme, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  Invoice_Read,
  InvoiceCategoryFilter,
  patchV1CompaniesCompanyIdJobsId,
  patchV1CompaniesCompanyIdJobsJobIdSummary,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import PrevailingWageBadge from '~components/Common/PrevailingWageBadge';
import JobSummaryEditForm, {
  JobSummaryEditFormHandler,
} from '~components/Job/JobSummaryEditForm';
import { RouteMap } from '~components/Job/RouteMap';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { Skeleton } from '~components/Skeleton/Skeleton';
import useAddOns from '~hooks/useAddOns/useAddOns';
import { trackApprovalBatch } from '~hooks/useAnalytics/trackApprovals';
import { DriverBasic } from '~hooks/useDrivers/models';
import { InvoiceLineItem } from '~hooks/useInvoiceLineItems/models';
import useInvoiceLineItems from '~hooks/useInvoiceLineItems/useInvoiceLineItems';
import { Invoice } from '~hooks/useInvoices/models';
import useInvoices from '~hooks/useInvoices/useInvoices';
import { Job, JobSummary } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import { useOrders } from '~hooks/useOrders/useOrders';
import { PhaseBasic } from '~hooks/useProjects/models';
import useResourceUsageLogs from '~hooks/useResourceUsageLogs/useResourceUsageLogs';
import CategoryApprovalsCard from '~pages/Approvals/DriverDay/components/CategoryApprovalsCard';
import ChargesTable from '~pages/Approvals/DriverDay/components/ChargesTable';
import { JobDetailsTabs } from '~pages/Approvals/DriverDay/components/JobDetails';
import { JobDetailsFormData, schemaHelpers } from '~pages/Approvals/DriverPay/helpers';
import { LoadsQueryContextProvider } from '~pages/Approvals/Files/components/JobsTable/ExpandedBlock/LoadsQueryContext';
import LoadsTab from '~pages/Approvals/Files/components/JobsTable/ExpandedBlock/LoadsTab';
import useApprovalCardShadow from '~pages/Approvals/shared/useApprovalCardShadow';
import { JobInvoices } from '~pages/Dispatch/JobDetailsContainer';
import connection from '~services/connectionModule';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEquipmentLabel } from '~utils/equipment';
import { getApprovalStatus } from '~utils/jobs/job-utils';
import {
  subscribeWithMultipleHandlers,
  unsubscribeHandler,
} from '~utils/rtu/rtu-multi-handler';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import CollapsibleSection from './CollapsibleSection';
import JobInvoiceActions from './JobInvoiceActions';
import JobSummaryHero from './JobSummaryHero';

// Option B: scope buttons inside shared children (Loads, the map/timeline rail) to
// the neutral outlined style used across this panel, without altering those shared
// components. Start neutral; visual weight is layered back in later.
// Strips the map card's built-in yellow so its buttons read as the app's standard
// outline treatment (gray-7 border, gray-1 label) like every other secondary action.
const neutralButtonsSx = (theme: Theme) => ({
  [`& .${buttonClasses.root}:not(.${buttonClasses.disabled})`]: {
    backgroundColor: 'transparent',
    color: theme.brandV2.colors.treadGray1,
    border: `1px solid ${theme.brandV2.colors.treadGray7}`,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: theme.brandV2.colors.treadGray8,
      borderColor: theme.brandV2.colors.treadGray7,
      boxShadow: 'none',
    },
  },
});

// Read-only label/value stat used in the Map + Hours workspace's Hours column.
function WorkspaceReadStat({ label, value }: { label: string; value: string }) {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '.06em',
          color: theme.brandV2.colors.treadGray3,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{ fontSize: '15px', fontWeight: 700, color: theme.brandV2.colors.treadBlack }}
      >
        {value}
      </Typography>
    </Box>
  );
}

interface JobDetailsProps {
  belowApprovals?: ReactNode;
  belowTimesheets?: ReactNode;
  columnWidths?: string;
  includeTimelineInJobDetailsTab?: boolean;
  job: Job;
  onJobUpdated?: (job: Job) => void;
}

const JobDetailsRedesigned = observer(
  ({
    belowApprovals,
    belowTimesheets,
    columnWidths = 'minmax(0, 8fr) minmax(0, 4fr)',
    includeTimelineInJobDetailsTab,
    job: initialJob,
    onJobUpdated,
  }: JobDetailsProps) => {
    const theme = useTheme();
    const cardShadow = useApprovalCardShadow();
    const { userStore, approveJobsStore, addOnsStore, ordersStore, toasterStore } =
      useStores();
    const companyId = getEffectiveUserCompanyId(userStore);
    const { getJobById, approveJob, unapproveJob, getJobLoads } = useJob();
    const { getJobPayable, getJobReceivable, approveInvoice, unapproveInvoice } =
      useInvoices();
    const { getAllInvoiceLineItems } = useInvoiceLineItems();
    const { getResourceUsageLogsByJob, batchUpdateResourceUsageLogsByJob } =
      useResourceUsageLogs();
    const {
      getAllAddOnChargesByJob,
      getAllInvoiceAddOnCharges,
      batchUpdateAddOnChargesByJob,
    } = useAddOns();
    const { getOrderPhasesTypeahead, getOrderById } = useOrders();

    const [job, setJob] = useState(initialJob);
    const [payable, setPayable] = useState<Invoice | null>(null);
    const [receivable, setReceivable] = useState<Invoice | null>(null);
    const [payableLineItems, setPayableLineItems] = useState<InvoiceLineItem[]>([]);
    const [receivableLineItems, setReceivableLineItems] = useState<InvoiceLineItem[]>([]);
    const [loadingInvoices, setLoadingInvoices] = useState(true);
    // Which side's editable invoice table is open in the slide-out drawer (null = closed).
    const [invoiceDrawerCategory, setInvoiceDrawerCategory] =
      useState<InvoiceCategoryFilter | null>(null);
    // Bumped when the drawer closes so the equation rows refetch and pick up edits.
    const [invoicesVersion, setInvoicesVersion] = useState(0);

    // The job's internal-notes snapshot goes stale on completed orders (notes edited
    // after order creation don't propagate to the job). Pull the LIVE value from the
    // full order so tax-exempt flags / rate reminders always show.
    const [liveInternalNotes, setLiveInternalNotes] = useState<string | null>(null);
    useEffect(() => {
      const orderId = job.order?.id;
      if (!orderId) {
        return;
      }
      const controller = new AbortController();
      getOrderById(orderId, controller.signal)
        .then((order) => {
          if (!controller.signal.aborted) {
            setLiveInternalNotes(order?.internalNotes ?? null);
          }
        })
        .catch(() => {});
      return () => controller.abort();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job.order?.id]);
    const internalNotes = liveInternalNotes ?? job.internalNotes;

    useEffect(() => {
      const controller = new AbortController();
      const { signal } = controller;
      setLoadingInvoices(true);

      const lineItemsFor = (invoice: Invoice | null) =>
        invoice && invoice.id
          ? getAllInvoiceLineItems(
              companyId,
              invoice.id,
              invoice.materialRateItemized,
              signal,
            )
          : Promise.resolve<InvoiceLineItem[]>([]);

      Promise.all([
        getJobPayable(job.id, companyId, { signal }),
        getJobReceivable(job.id, companyId, { signal }),
      ])
        .then(async ([payableResult, receivableResult]) => {
          const nextPayable = payableResult ?? null;
          const nextReceivable = receivableResult ?? null;
          const [nextPayableLineItems, nextReceivableLineItems] = await Promise.all([
            lineItemsFor(nextPayable),
            lineItemsFor(nextReceivable),
          ]);
          if (signal.aborted) {
            return;
          }
          setPayable(nextPayable);
          setReceivable(nextReceivable);
          setPayableLineItems(nextPayableLineItems);
          setReceivableLineItems(nextReceivableLineItems);
        })
        .catch((error) => {
          if (signal.aborted) {
            return;
          }

          throw error;
        })
        .finally(() => {
          if (!signal.aborted) {
            setLoadingInvoices(false);
          }
        });

      return () => controller.abort();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job.id, companyId, invoicesVersion]);

    // Keep the latest invoices in refs so the long-lived RTU handler below reads
    // current values without re-subscribing on every edit.
    const payableRef = useRef<Invoice | null>(null);
    const receivableRef = useRef<Invoice | null>(null);
    useEffect(() => {
      payableRef.current = payable;
      receivableRef.current = receivable;
    }, [payable, receivable]);

    // Live AR/AP: the backend recomputes invoices asynchronously after ticket, load,
    // or hours edits and broadcasts on InvoiceUpdateChannel. Subscribe (same pattern
    // as the Dispatch panel) and update the matching side + its line items so the
    // summary's Receivable & Payable column reflects edits without a manual refresh.
    useEffect(() => {
      const key = `approve_jobs_summary_${job.id}_${Date.now()}`;
      subscribeWithMultipleHandlers(
        realTimeChannels.InvoiceUpdateChannel,
        companyId,
        key,
        ({ data }: { data: Invoice_Read }) => {
          const updated = Invoice.parse(data);
          if (updated.job?.id !== job.id) {
            return;
          }
          const refetchLineItems = (invoice: Invoice) =>
            invoice.id
              ? getAllInvoiceLineItems(
                  companyId,
                  invoice.id,
                  invoice.materialRateItemized,
                )
              : Promise.resolve<InvoiceLineItem[]>([]);

          if (updated.id === receivableRef.current?.id) {
            setReceivable(updated);
            refetchLineItems(updated).then(setReceivableLineItems);
            getAllInvoiceAddOnCharges(companyId, String(updated.id));
          } else if (updated.id === payableRef.current?.id) {
            setPayable(updated);
            refetchLineItems(updated).then(setPayableLineItems);
            getAllInvoiceAddOnCharges(companyId, String(updated.id));
          } else {
            // A side we don't have yet (e.g. the invoice was just created) — refetch both.
            setInvoicesVersion((version) => version + 1);
          }
        },
        { job_id: job.id },
      );

      return () => {
        unsubscribeHandler(realTimeChannels.InvoiceUpdateChannel, companyId, key, {
          job_id: job.id,
        });
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job.id, companyId]);

    // Per-side add-on charges (names) — the invoice model only carries an aggregate
    // count/total, so fetch each side's add-on charges to reveal which are applied.
    useEffect(() => {
      if (receivable?.id) {
        getAllInvoiceAddOnCharges(companyId, receivable.id);
      }
      if (payable?.id) {
        getAllInvoiceAddOnCharges(companyId, payable.id);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [receivable?.id, payable?.id, companyId]);

    const receivableAddOns =
      addOnsStore.addOnChargesByInvoiceId[receivable?.id ?? ''] ?? [];
    const payableAddOns = addOnsStore.addOnChargesByInvoiceId[payable?.id ?? ''] ?? [];

    const handleJobUpdated = useCallback(
      (job: Job) => {
        setJob(job);
        approveJobsStore.upsertJob(job);

        onJobUpdated?.(job);
      },
      [onJobUpdated],
    );

    // Per-side approve / unapprove for the Summary's Receivable & Payable column.
    const handleApproveSide = useCallback(
      (side: InvoiceCategoryFilter) => {
        if (side === InvoiceCategoryFilter.PAYABLES && payable) {
          approveInvoice(payable).then((updated) => updated && setPayable(updated));
        } else if (side === InvoiceCategoryFilter.RECEIVABLES && receivable) {
          approveInvoice(receivable).then((updated) => updated && setReceivable(updated));
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [payable, receivable],
    );

    const handleUnapproveSide = useCallback(
      async (side: InvoiceCategoryFilter) => {
        if (side === InvoiceCategoryFilter.PAYABLES && payable) {
          const updated = await unapproveInvoice(payable);
          if (updated) {
            setPayable(updated);
          }
        } else if (side === InvoiceCategoryFilter.RECEIVABLES && receivable) {
          const updated = await unapproveInvoice(receivable);
          if (updated) {
            setReceivable(updated);
          }
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [payable, receivable],
    );

    // --- Job (field) approval — permission-scoped into the summary cluster ---
    const userPermissions = userStore.getPermissions();
    const [isJobApprovalLoading, setIsJobApprovalLoading] = useState(false);
    const {
      isApproved: jobApproved,
      isApprovable: jobApprovable,
      isUnapprovable: jobUnapprovable,
    } = getApprovalStatus(job, userPermissions, Boolean(job.order?.editable));

    const handleApproveJob = useCallback(() => {
      setIsJobApprovalLoading(true);
      approveJob(job.id, companyId)
        .then((updated) => {
          if (updated) {
            trackApprovalBatch('job', 1);
            handleJobUpdated(updated);
          }
        })
        .finally(() => setIsJobApprovalLoading(false));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job.id, companyId, handleJobUpdated]);

    const handleUnapproveJob = useCallback(() => {
      setIsJobApprovalLoading(true);
      unapproveJob(job.id, companyId)
        .then((updated) => updated && handleJobUpdated(updated))
        .finally(() => setIsJobApprovalLoading(false));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job.id, companyId, handleJobUpdated]);

    // Billing approvals (Rec/Pay) are scoped to billing approvers.
    const showBillingApprovals = userPermissions.canBillingApprove;
    // Job-approve is one action gated by canApproveJob — same permission the grid row
    // and the rest of the app use. De-emphasized in the cluster as a compact "Job"
    // button beside Rec/Pay, not a prominent header CTA.
    const jobApprovalConfig = userPermissions.canApproveJob
      ? {
          label: t('approvals.driver_day.job'),
          approved: jobApproved,
          approvable: jobApprovable,
          unapprovable: jobUnapprovable,
          loading: isJobApprovalLoading,
          onApprove: handleApproveJob,
          onUnapprove: handleUnapproveJob,
        }
      : null;

    // --- Job summary edit — times + the job-level phase ---
    const [isEditingSummary, setIsEditingSummary] = useState(false);
    const [isSubmittingSummary, setIsSubmittingSummary] = useState(false);
    // Map + hours workspace — larger route replay beside the hour editor.
    const [workspaceOpen, setWorkspaceOpen] = useState(false);
    // Workspace Hours start read-only; the pencil flips to the edit form.
    const [isEditingWorkspaceHours, setIsEditingWorkspaceHours] = useState(false);
    const summaryFormRef = useRef<JobSummaryEditFormHandler | null>(null);
    const canEditSummary = Boolean(job.jobSummary?.editable);
    // Phase-code / charges editing follows the job-level editable flag (matching legacy
    // Approve Jobs), which differs from the summary-times editable flag above.
    const canEditCharges = Boolean(job.editable);

    // Loads + phase-code hours open in modals (keeps the card compact rather than
    // expanding inline). The loads stat in the hero is the loads-modal trigger.
    const [loadsModalOpen, setLoadsModalOpen] = useState(false);
    // No per-job approved-loads count on the model — derive it from the fetched loads
    // using the same approval logic the loads table uses.
    const [approvedLoadsCount, setApprovedLoadsCount] = useState<number | null>(null);

    const refreshApprovedLoadsCount = useCallback(async () => {
      const loads = await getJobLoads(job.id);
      if (!loads) {
        return;
      }
      const count = loads.filter(
        (load) =>
          getApprovalStatus(load, userPermissions, Boolean(job.order?.editable))
            .isApproved,
      ).length;
      setApprovedLoadsCount(count);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job.id]);

    useEffect(() => {
      refreshApprovedLoadsCount();
    }, [refreshApprovedLoadsCount]);

    const phaseToBasic = (): PhaseBasic | null =>
      job.phase
        ? new PhaseBasic(job.phase.id, job.phase.name, job.phase.code ?? null)
        : null;
    const [selectedPhase, setSelectedPhase] = useState<PhaseBasic | null>(phaseToBasic());

    const startEditingSummary = useCallback(() => {
      setSelectedPhase(phaseToBasic());
      setIsEditingSummary(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job.phase?.id]);

    // Open the map + hours workspace. Seed selectedPhase (so the shared save's phase
    // compare is a no-op unless changed) and let the drawer host the summary form.
    const openWorkspace = useCallback(() => {
      setSelectedPhase(phaseToBasic());
      setIsEditingWorkspaceHours(false);
      setWorkspaceOpen(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job.phase?.id]);

    // Don't silently discard in-progress hour edits when the workspace is dismissed
    // (backdrop, Escape, or the header X). Require an explicit Save/Cancel in the hours
    // editor first — matching how the phase-code edits are never lost on dismiss.
    const closeWorkspace = useCallback(() => {
      if (isEditingWorkspaceHours) {
        return;
      }
      setWorkspaceOpen(false);
    }, [isEditingWorkspaceHours]);

    const fmtWorkspaceTime = (d?: Dayjs | null): string =>
      d ? dayjs.tz(d).format('h:mm A') : '-';

    // Notes editing — internal + driver notes edited together from the Notes section.
    const [isEditingNotes, setIsEditingNotes] = useState(false);
    const [isSavingNotes, setIsSavingNotes] = useState(false);
    const [internalNotesDraft, setInternalNotesDraft] = useState('');
    const [driverNotesDraft, setDriverNotesDraft] = useState('');

    const startEditingNotes = () => {
      setInternalNotesDraft(internalNotes ?? '');
      setDriverNotesDraft(job.notes ?? '');
      setIsEditingNotes(true);
    };

    const saveNotes = async () => {
      setIsSavingNotes(true);
      try {
        await patchV1CompaniesCompanyIdJobsId({
          path: { 'company-id': companyId, id: job.id },
          body: {
            internal_notes: internalNotesDraft || null,
            notes: driverNotesDraft || null,
          },
        });
        // The Notes display prefers live order notes; reflect the internal edit locally.
        setLiveInternalNotes(internalNotesDraft || null);
        await handleReloadJob();
        toasterStore.push(alert(t('success_messages.changes_saved'), AlertTypes.success));
        setIsEditingNotes(false);
      } catch (error) {
        connection.handleRequestError(error, t('error_messages.jobs.failed_to_update'));
      } finally {
        setIsSavingNotes(false);
      }
    };

    // Shared icon-only edit button style (Notes + Hours-by-Phase-Code headers).
    const editIconSx = {
      width: 30,
      height: 30,
      p: 0,
      border: `1px solid ${theme.brandV2.colors.treadGray7}`,
      borderRadius: '4px',
      color: theme.brandV2.colors.treadGray2,
      '&:hover': {
        borderColor: theme.brandV2.colors.treadGray3,
        backgroundColor: theme.brandV2.colors.treadGray8,
        color: theme.brandV2.colors.treadBlack,
      },
    } as const;

    // Workspace Hours read-only values.
    const wsStart =
      job.jobSummary?.adjustedLoggingStartedAt ?? job.jobSummary?.loggingStartedAt;
    const wsEnd =
      job.jobSummary?.adjustedLoggingEndedAt ?? job.jobSummary?.loggingEndedAt;
    const wsMinutes =
      job.jobSummary?.approvedWorkTimeMinutes ?? job.jobSummary?.workTimeMinutes;
    const wsTotalHrs =
      typeof wsMinutes === 'number' ? `${(wsMinutes / 60).toFixed(2)}h` : '-';
    const wsPhaseLabel = job.phase
      ? job.phase.code
        ? `${job.phase.name} (${job.phase.code})`
        : job.phase.name
      : (job.jobSummary?.jobPhaseName ?? '-');

    // Returns true only when the summary actually saved. On validation failure or a
    // failed request we keep the editor open (and its typed values) rather than
    // silently discarding the edit. Callers (hero + workspace) close edit mode only
    // when this resolves true.
    const handleSubmitSummary = useCallback(async (): Promise<boolean> => {
      const formData = await summaryFormRef.current?.onSubmit();
      if (!formData) {
        return false;
      }
      setIsSubmittingSummary(true);
      try {
        const summaryResponse = await patchV1CompaniesCompanyIdJobsJobIdSummary({
          path: { 'company-id': companyId, 'job-id': job.id },
          body: {
            adjusted_logging_started_at: formData.loggingStartedAt?.toISOString() ?? null,
            adjusted_logging_ended_at: formData.loggingEndedAt?.toISOString() ?? null,
            approved_work_time_minutes: formData.approvedWorkTimeMinutes,
          },
        });
        const nextSummary = JobSummary.parse(summaryResponse.data.data);

        // Persist the job-level phase if it changed (targeted phase_id patch).
        let updatedJob = job;
        if ((selectedPhase?.id ?? null) !== (job.phase?.id ?? null)) {
          const jobResponse = await patchV1CompaniesCompanyIdJobsId({
            path: { 'company-id': companyId, id: job.id },
            body: { phase_id: selectedPhase?.id ?? null },
          });
          updatedJob = Job.parse(jobResponse.data.data);
        }
        updatedJob.jobSummary = nextSummary;
        handleJobUpdated(updatedJob);
        toasterStore.push(
          alert(t('approvals.approve_jobs.job_summary_updated'), AlertTypes.success),
        );
        setIsEditingSummary(false);
        // Summary-hours edits cascade to the resource-usage-logs server-side; refetch
        // so "Hours by Phase Code" reflects the new values without a reload. Kept
        // out of the save's try/catch (fire-and-forget with its own catch): the save
        // already succeeded, so a failed refetch must not fire the error toast or
        // hold the editor open — it just leaves the rows stale until the next load.
        void Promise.all([
          getResourceUsageLogsByJob(companyId, job.id),
          getAllAddOnChargesByJob(companyId, job.id),
        ]).catch(() => {});
        return true;
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.jobs.failed_to_update_job_summary'),
        );
        return false;
      } finally {
        setIsSubmittingSummary(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, job, selectedPhase, handleJobUpdated]);

    const handleReloadJob = useCallback(async () => {
      approveJobsStore.updateJobStart(job.id);

      const updatedJob = await getJobById(job.id, companyId);

      if (updatedJob) {
        setJob(updatedJob);
        approveJobsStore.updateJobSuccess(updatedJob);
        onJobUpdated?.(updatedJob);
      }
    }, [job.id, companyId, onJobUpdated]);

    useEffect(() => {
      setJob(initialJob);
    }, [initialJob]);

    // Hours data (resource usage logs + add-on charges) → read reactively from the store.
    const [loadingResourceLogs, setLoadingResourceLogs] = useState(true);
    useEffect(() => {
      setLoadingResourceLogs(true);
      void Promise.all([
        getResourceUsageLogsByJob(companyId, job.id),
        getAllAddOnChargesByJob(companyId, job.id),
      ])
        .catch(() => {})
        .finally(() => setLoadingResourceLogs(false));
    }, [job.id, companyId]);

    const resourceUsageLogs = useMemo(() => {
      return addOnsStore.resourceUsageLogsByJobId[job.id] ?? [];
    }, [job.id, addOnsStore.resourceUsageLogsByJobId]);
    const addOns = addOnsStore.addOnChargesByJobId[job.id] ?? [];

    // --- Hours / Add-ons edit (reuses the shared ChargesTable harness) ---
    const orderId = job.order?.id ?? '';
    // Direct read (not useMemo): the store mutates this map in place, so as an
    // observer we re-render on change and read the fresh array each render.
    const phasesOptions = ordersStore.phaseTypeheadsByOrderID[orderId] ?? [];
    const reloadOrderPhases = useCallback(async () => {
      await getOrderPhasesTypeahead({
        companyId,
        orderId,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId, companyId]);

    useEffect(() => {
      if (orderId) {
        getOrderPhasesTypeahead({
          companyId,
          orderId,
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId, companyId]);

    const [isEditingHours, setIsEditingHours] = useState(false);
    const chargesForm = useForm({
      resolver: yupResolver(JobDetailsFormData),
      defaultValues: {
        addOnCharges: addOns.map(schemaHelpers.addOnChargeToAddOnChargeDTO),
        resourceUsageLogs: resourceUsageLogs.map((log) =>
          schemaHelpers.resourceUsageLogToResourceUsageLogDTO(log),
        ),
      },
      mode: 'all',
      delayError: 500,
    });
    const {
      handleSubmit: handleSubmitCharges,
      reset: resetCharges,
      setValue: setChargesValue,
      formState: { isDirty: chargesDirty },
    } = chargesForm;

    // Keep the form synced with the store while NOT editing (don't clobber edits).
    useEffect(() => {
      if (!isEditingHours) {
        setChargesValue(
          'addOnCharges',
          addOns.map(schemaHelpers.addOnChargeToAddOnChargeDTO),
        );
        setChargesValue(
          'resourceUsageLogs',
          resourceUsageLogs.map((log) =>
            schemaHelpers.resourceUsageLogToResourceUsageLogDTO(log),
          ),
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addOns, resourceUsageLogs, isEditingHours]);

    const cancelEditingHours = useCallback(() => {
      resetCharges({
        addOnCharges: addOns.map(schemaHelpers.addOnChargeToAddOnChargeDTO),
        resourceUsageLogs: resourceUsageLogs.map((log) =>
          schemaHelpers.resourceUsageLogToResourceUsageLogDTO(log),
        ),
      });
      setIsEditingHours(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addOns, resourceUsageLogs, resetCharges]);

    const [isSubmittingCharges, setIsSubmittingCharges] = useState(false);
    const onSubmitCharges = handleSubmitCharges(async (data) => {
      if (!chargesDirty) {
        setIsEditingHours(false);
        return;
      }
      setIsSubmittingCharges(true);
      try {
        await Promise.all([
          batchUpdateResourceUsageLogsByJob(
            companyId,
            job.id,
            data.resourceUsageLogs,
            job.jobStartAt.toISOString(),
          ),
          batchUpdateAddOnChargesByJob(companyId, job.id, data.addOnCharges),
        ]);
        await Promise.all([
          getResourceUsageLogsByJob(companyId, job.id),
          getAllAddOnChargesByJob(companyId, job.id),
        ]);
        setIsEditingHours(false);
        toasterStore.push(alert(t('success_messages.changes_saved'), AlertTypes.success));
      } catch (error) {
        connection.handleRequestError(error, t('error_messages.jobs.failed_to_update'));
      } finally {
        setIsSubmittingCharges(false);
      }
    });

    // The job resource-usage-logs (phase-code) editor — rendered inline in the
    // Hours-by-Phase-Code section.
    const chargesEditor = (
      <FormProvider {...chargesForm}>
        <ChargesTable
          addOns={addOns}
          driver={job.driver as DriverBasic}
          hideCreatePhase
          isEditing={isEditingHours}
          isJobLevel
          orderId={orderId}
          owningCompanyId={companyId}
          phasesOptions={phasesOptions}
          reloadOrderPhases={reloadOrderPhases}
          resourceUsageLogs={resourceUsageLogs}
        />
      </FormProvider>
    );

    return (
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: columnWidths,
          alignItems: 'start',
          maxWidth: `calc(100% - ${theme.spacing(2)})`,
        }}
      >
        {/* ---- main column: white card with header + stacked sections ---- */}
        <Box
          sx={{
            bgcolor: 'background.paper',
            border: `1px solid ${theme.brandV2.colors.treadGray7}`,
            borderRadius: 2,
            boxShadow: cardShadow,
            minWidth: 0,
          }}
        >
          {/* Sections stack flush; each CollapsibleSection carries a full-width top
              divider, so the rules bleed edge-to-edge. Horizontal inset lives inside
              each section (px: 2), not on this container. */}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {/* SUMMARY (always shown) — no section padding; the hero fills the card
                edge-to-edge, only the header keeps a small inset. */}
            <Box sx={{ pt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5 }}>
                <Summarize
                  sx={{ fontSize: 18, color: theme.brandV2.colors.treadGray3 }}
                />
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: theme.brandV2.colors.treadBlack,
                  }}
                >
                  {t('approvals.summary')}
                </Typography>
                {/* Prevailing-wage flag surfaced from the order — signals the reviewer
                    to break out hourly-paid driver hours, without scrolling to the order. */}
                {job.order?.prevailingWage && <PrevailingWageBadge />}

                <Box
                  sx={{
                    ml: 'auto',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  {/* Save/Cancel live in a footer near the fields (below); the header
                      shows the approve cluster only when not editing. */}
                  {!isEditingSummary && (
                    <JobInvoiceActions
                      jobApproval={jobApprovalConfig}
                      loadingInvoices={loadingInvoices}
                      onApproveSide={handleApproveSide}
                      onUnapproveSide={handleUnapproveSide}
                      payable={payable}
                      receivable={receivable}
                      showBillingApprovals={showBillingApprovals}
                    />
                  )}
                </Box>
              </Box>
              {/* Edit-in-place: the hero always renders; when editing, the Start/End/
                  Total fields and the Phase field become inputs at their positions
                  while the delivered/financial columns stay visible. */}
              <JobSummaryHero
                approvedLoadsCount={approvedLoadsCount}
                canEditHours={canEditSummary}
                companyId={companyId}
                isEditing={isEditingSummary && !workspaceOpen}
                isSubmittingForm={isSubmittingSummary}
                job={job}
                loadingInvoices={loadingInvoices}
                onCancelEdit={() => setIsEditingSummary(false)}
                onEditHours={startEditingSummary}
                onEditPayable={() =>
                  setInvoiceDrawerCategory(InvoiceCategoryFilter.PAYABLES)
                }
                onEditReceivable={() =>
                  setInvoiceDrawerCategory(InvoiceCategoryFilter.RECEIVABLES)
                }
                onOpenLoads={() => setLoadsModalOpen(true)}
                onSaveEdit={handleSubmitSummary}
                onSummarySaved={handleReloadJob}
                onSelectPhase={setSelectedPhase}
                payable={payable}
                payableAddOns={payableAddOns}
                payableLineItems={payableLineItems}
                phaseOptions={phasesOptions}
                receivable={receivable}
                receivableAddOns={receivableAddOns}
                receivableLineItems={receivableLineItems}
                selectedPhase={selectedPhase}
                summaryFormRef={summaryFormRef}
              />
            </Box>

            {/* NOTES — driver + internal notes (context; read-only). Placed above
                Hours by Phase Code and expanded by default so the context reads
                first. Shown only when the job actually has notes. */}
            {(internalNotes || job.notes) && (
              <CollapsibleSection
                title={t('form_fields.notes')}
                icon={<StickyNote2 sx={{ fontSize: 16 }} />}
                defaultExpanded
                headerActions={({ expand }) =>
                  isEditingNotes ? (
                    <>
                      <SmallButton
                        color="inherit"
                        disableElevation
                        onClick={() => setIsEditingNotes(false)}
                        size="small"
                        variant="outlined"
                        sx={{ border: `1px solid ${theme.brandV2.colors.treadGray7}` }}
                      >
                        {t('actions.cancel')}
                      </SmallButton>
                      <SmallLoadingButton
                        color="brandV2Green"
                        loading={isSavingNotes}
                        onClick={saveNotes}
                        size="small"
                        startIcon={<Check />}
                        variant="contained"
                        sx={{ fontWeight: 700 }}
                      >
                        {t('actions.save')}
                      </SmallLoadingButton>
                    </>
                  ) : (
                    <Tooltip title={t('actions.edit')} placement="bottom">
                      <IconButton
                        aria-label={t('actions.edit')}
                        onClick={() => {
                          expand();
                          startEditingNotes();
                        }}
                        size="small"
                        sx={editIconSx}
                      >
                        <Edit sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )
                }
              >
                {isEditingNotes ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '11px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '.06em',
                          color: theme.brandV2.colors.treadGray3,
                          mb: 0.5,
                        }}
                      >
                        {t('form_fields.internal_notes')}
                      </Typography>
                      <TextField
                        disabled={isSavingNotes}
                        fullWidth
                        minRows={2}
                        multiline
                        onChange={(e) => setInternalNotesDraft(e.target.value)}
                        placeholder={t('form_fields.internal_notes_placeholder')}
                        size="small"
                        value={internalNotesDraft}
                      />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '11px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '.06em',
                          color: theme.brandV2.colors.treadGray3,
                          mb: 0.5,
                        }}
                      >
                        {t('loads.driver_notes')}
                      </Typography>
                      <TextField
                        disabled={isSavingNotes}
                        fullWidth
                        minRows={2}
                        multiline
                        onChange={(e) => setDriverNotesDraft(e.target.value)}
                        size="small"
                        value={driverNotesDraft}
                      />
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    {internalNotes ? (
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '11px',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '.06em',
                            color: theme.brandV2.colors.treadGray3,
                            mb: 0.5,
                          }}
                        >
                          {t('form_fields.internal_notes')}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '13px',
                            color: theme.brandV2.colors.treadGray1,
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {internalNotes}
                        </Typography>
                      </Box>
                    ) : null}
                    {job.notes ? (
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '11px',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '.06em',
                            color: theme.brandV2.colors.treadGray3,
                            mb: 0.5,
                          }}
                        >
                          {t('loads.driver_notes')}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '13px',
                            color: theme.brandV2.colors.treadGray1,
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {job.notes}
                        </Typography>
                      </Box>
                    ) : null}
                  </Box>
                )}
              </CollapsibleSection>
            )}

            {/* HOURS BY PHASE CODE — inline, expandable (expanded by default) */}
            <CollapsibleSection
              title={t('approvals.approve_jobs.summary_hero.hours_by_phase_code')}
              icon={<Schedule sx={{ fontSize: 16 }} />}
              headerActions={({ expand }) =>
                canEditCharges && !workspaceOpen ? (
                  isEditingHours ? (
                    <>
                      <SmallButton
                        color="inherit"
                        disableElevation
                        onClick={cancelEditingHours}
                        size="small"
                        variant="outlined"
                        sx={{ border: `1px solid ${theme.brandV2.colors.treadGray7}` }}
                      >
                        {t('actions.cancel')}
                      </SmallButton>
                      <SmallLoadingButton
                        color="brandV2Green"
                        loading={isSubmittingCharges}
                        onClick={onSubmitCharges}
                        size="small"
                        startIcon={<Check />}
                        variant="contained"
                        sx={{ fontWeight: 700 }}
                      >
                        {t('actions.save')}
                      </SmallLoadingButton>
                    </>
                  ) : (
                    <Tooltip title={t('actions.edit')} placement="bottom">
                      <IconButton
                        aria-label={t('actions.edit')}
                        onClick={() => {
                          expand();
                          setIsEditingHours(true);
                        }}
                        size="small"
                        sx={editIconSx}
                      >
                        <Edit sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )
                ) : null
              }
            >
              {workspaceOpen ? null : loadingResourceLogs &&
                resourceUsageLogs.length === 0 ? (
                <Box
                  data-test-id="charges-skeleton"
                  sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 1 }}
                >
                  {[0, 1, 2].map((row) => (
                    <Box key={row} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Skeleton variant="text" width={90} height={14} />
                      <Skeleton variant="text" height={14} sx={{ flex: 1 }} />
                      <Skeleton variant="text" width={48} height={14} />
                      <Skeleton variant="text" width={120} height={14} />
                    </Box>
                  ))}
                </Box>
              ) : (
                chargesEditor
              )}
            </CollapsibleSection>

            {/* Receivable / Payable reconciliation now lives in the Summary's
                financial column (JobSummaryHero → JobMarginBreakdown). The slide-out
                editor below is still opened by that column's per-side Edit menu. */}
            <Drawer
              anchor="right"
              onClose={() => {
                setInvoiceDrawerCategory(null);
                setInvoicesVersion((version) => version + 1);
              }}
              open={invoiceDrawerCategory !== null}
              variant="temporary"
              sx={{
                [`& .${drawerClasses.paper}`]: {
                  maxWidth: '1280px',
                  overflow: 'hidden',
                  width: '90%',
                },
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box
                  sx={{
                    alignItems: 'center',
                    borderBottom: `1px solid ${theme.brandV2.colors.treadGray7}`,
                    display: 'flex',
                    flex: 0,
                    justifyContent: 'space-between',
                    p: 2,
                  }}
                >
                  <Typography sx={{ fontSize: '16px', fontWeight: 700 }}>
                    {invoiceDrawerCategory === InvoiceCategoryFilter.PAYABLES
                      ? t('dispatch.job.payables')
                      : t('dispatch.job.receivables')}
                  </Typography>
                  <IconButton
                    onClick={() => {
                      setInvoiceDrawerCategory(null);
                      setInvoicesVersion((version) => version + 1);
                    }}
                    size="small"
                  >
                    <Close />
                  </IconButton>
                </Box>
                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                  {invoiceDrawerCategory !== null && (
                    <JobInvoices
                      currentCategory={invoiceDrawerCategory}
                      hideMap
                      job={job}
                      reloadAfterApproved
                    />
                  )}
                </Box>
              </Box>
            </Drawer>

            {/* Map + Hours workspace — larger route replay beside the hour editor,
                opened from the hero's Map button. */}
            <Drawer
              anchor="right"
              open={workspaceOpen}
              onClose={closeWorkspace}
              variant="temporary"
              sx={{
                [`& .${drawerClasses.paper}`]: {
                  height: '100dvh',
                  maxWidth: '1440px',
                  overflow: 'hidden',
                  width: '92%',
                },
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box
                  sx={{
                    alignItems: 'center',
                    borderBottom: `1px solid ${theme.brandV2.colors.treadGray7}`,
                    display: 'flex',
                    flex: 0,
                    justifyContent: 'space-between',
                    p: 2,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700 }}>
                      {job.order?.name ?? t('approvals.approve_jobs.summary_hero.hours')}
                    </Typography>
                    {job.driver?.fullName || job.equipment ? (
                      <Typography
                        sx={{ fontSize: 12, color: theme.brandV2.colors.treadGray2 }}
                      >
                        {[
                          job.driver?.fullName,
                          job.equipment ? getEquipmentLabel(job.equipment) : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>
                    ) : null}
                  </Box>
                  <IconButton onClick={closeWorkspace} size="small">
                    <Close />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
                  {/* Hours + phase-code editing — left. Takes 60% (not an even split):
                      the shared ChargesTable is sized for the full-width inline section,
                      so at half of this drawer its phase name / description inputs
                      truncate. 60% lands it within ~100px of the inline width. */}
                  <Box
                    sx={{
                      flex: '1 1 60%',
                      minWidth: 0,
                      borderRight: `1px solid ${theme.brandV2.colors.treadGray7}`,
                      overflow: 'auto',
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 3,
                    }}
                  >
                    {/* Hours — read-only by default (Start/End/Total 3-col + phase);
                        the pencil flips to the edit form. */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                          {t('approvals.approve_jobs.summary_hero.hours')}
                        </Typography>
                        {isEditingWorkspaceHours ? (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <SmallButton
                              color="inherit"
                              disableElevation
                              // Disable Cancel while a save is in flight: the payload is
                              // already captured, so a mid-save cancel only unmounts the
                              // form while the PATCH still persists — misleading the user.
                              disabled={isSubmittingSummary}
                              onClick={() => setIsEditingWorkspaceHours(false)}
                              size="small"
                              variant="outlined"
                              sx={{
                                border: `1px solid ${theme.brandV2.colors.treadGray7}`,
                              }}
                            >
                              {t('actions.cancel')}
                            </SmallButton>
                            <SmallLoadingButton
                              color="brandV2Green"
                              loading={isSubmittingSummary}
                              onClick={async () => {
                                // Only leave edit mode when the save actually
                                // succeeded — keep the form (and its values +
                                // validation errors) open on failure.
                                const saved = await handleSubmitSummary();
                                if (saved) {
                                  setIsEditingWorkspaceHours(false);
                                }
                              }}
                              size="small"
                              startIcon={<Check />}
                              variant="contained"
                              sx={{ fontWeight: 700 }}
                            >
                              {t('actions.save')}
                            </SmallLoadingButton>
                          </Box>
                        ) : canEditSummary ? (
                          <Tooltip title={t('actions.edit')} placement="bottom">
                            <IconButton
                              aria-label={t('actions.edit')}
                              onClick={() => setIsEditingWorkspaceHours(true)}
                              size="small"
                              sx={editIconSx}
                            >
                              <Edit sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </Box>

                      {isEditingWorkspaceHours ? (
                        <JobSummaryEditForm
                          ref={summaryFormRef}
                          approvedWorkTimeMinutes={wsMinutes ?? null}
                          approvedWorkTimeMinutesText={`${t('approvals.driver_day.breaks')}: ${job.jobSummary?.breakTimeMinutes ?? 0}min`}
                          captionLabels
                          isSubmittingForm={isSubmittingSummary}
                          loggingEndedAt={wsEnd ?? null}
                          loggingEndedAtText={`${t('approvals.driver_day.last_geofence_entry')}: ${fmtWorkspaceTime(job.jobSummary?.lastGeofenceExitAt)}`}
                          loggingStartedAt={wsStart ?? null}
                          loggingStartedAtText={`${t('approvals.driver_day.first_geofence_entry')}: ${fmtWorkspaceTime(job.jobSummary?.firstGeofenceEntryAt)}`}
                        />
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(3, 1fr)',
                              gap: 2,
                            }}
                          >
                            <WorkspaceReadStat
                              label={t(
                                'approvals.approve_jobs.grid_expanded_block.job_tab.job_start',
                              )}
                              value={fmtWorkspaceTime(wsStart)}
                            />
                            <WorkspaceReadStat
                              label={t(
                                'approvals.approve_jobs.grid_expanded_block.job_tab.job_end',
                              )}
                              value={fmtWorkspaceTime(wsEnd)}
                            />
                            <WorkspaceReadStat
                              label={t('approvals.driver_day.total_hrs')}
                              value={wsTotalHrs}
                            />
                          </Box>
                          <WorkspaceReadStat
                            label={t('approvals.approve_jobs.summary_hero.phase')}
                            value={wsPhaseLabel}
                          />
                        </Box>
                      )}
                    </Box>

                    {/* Phase codes (job resource usage logs) */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        borderTop: `1px solid ${theme.brandV2.colors.treadGray7}`,
                        pt: 2.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                        }}
                      >
                        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                          {t('approvals.approve_jobs.summary_hero.hours_by_phase_code')}
                        </Typography>
                        {canEditCharges ? (
                          isEditingHours ? (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <SmallButton
                                color="inherit"
                                disableElevation
                                onClick={cancelEditingHours}
                                size="small"
                                variant="outlined"
                                sx={{
                                  border: `1px solid ${theme.brandV2.colors.treadGray7}`,
                                }}
                              >
                                {t('actions.cancel')}
                              </SmallButton>
                              <SmallLoadingButton
                                color="brandV2Green"
                                loading={isSubmittingCharges}
                                onClick={onSubmitCharges}
                                size="small"
                                startIcon={<Check />}
                                variant="contained"
                                sx={{ fontWeight: 700 }}
                              >
                                {t('actions.save')}
                              </SmallLoadingButton>
                            </Box>
                          ) : (
                            <SmallButton
                              color="inherit"
                              disableElevation
                              onClick={() => setIsEditingHours(true)}
                              size="small"
                              startIcon={<Edit />}
                              variant="outlined"
                              sx={{
                                border: `1px solid ${theme.brandV2.colors.treadGray7}`,
                              }}
                            >
                              {t('actions.edit')}
                            </SmallButton>
                          )
                        ) : null}
                      </Box>
                      {workspaceOpen ? chargesEditor : null}
                    </Box>
                  </Box>
                  {/* Route replay — right */}
                  <Box sx={{ flex: '1 1 40%', minWidth: 0 }}>
                    {workspaceOpen && (
                      <RouteMap
                        job={job}
                        defaultTimelineVisible
                        sx={{ height: '100%' }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Drawer>

            {/* Loads — right-hand side drawer (same slide-out treatment as the
                Create Order form), hosting the Loads-tab table (LoadsTab). */}
            <Drawer
              anchor="right"
              onClose={() => {
                setLoadsModalOpen(false);
                refreshApprovedLoadsCount();
                handleReloadJob();
              }}
              open={loadsModalOpen}
              variant="temporary"
              sx={{
                [`& .${drawerClasses.paper}`]: {
                  height: '100dvh',
                  maxWidth: '1200px',
                  overflow: 'hidden',
                  width: '80%',
                },
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box
                  sx={{
                    alignItems: 'center',
                    borderBottom: `1px solid ${theme.brandV2.colors.treadGray7}`,
                    display: 'flex',
                    flex: 0,
                    justifyContent: 'space-between',
                    p: 2,
                  }}
                >
                  <Typography sx={{ fontSize: '16px', fontWeight: 700 }}>
                    {t('approvals.approve_jobs.summary_hero.loads')}
                  </Typography>
                  <IconButton
                    onClick={() => {
                      setLoadsModalOpen(false);
                      refreshApprovedLoadsCount();
                      handleReloadJob();
                    }}
                    size="small"
                  >
                    <Close />
                  </IconButton>
                </Box>
                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                  {loadsModalOpen && (
                    <LoadsQueryContextProvider companyId={companyId} job={job}>
                      <LoadsTab />
                    </LoadsQueryContextProvider>
                  )}
                </Box>
              </Box>
            </Drawer>
          </Box>
        </Box>

        {/* ---- right rail ---- */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            ...(belowTimesheets ? { gridRow: 'span 2' } : {}),
          }}
        >
          <CategoryApprovalsCard
            billingApprovedAt={job?.billingApprovedAt}
            fieldApprovedAt={job?.fieldApprovedAt}
            jobId={job.id}
          />
          {belowApprovals}

          <Box sx={neutralButtonsSx}>
            <JobDetailsTabs
              toggledJob={job}
              showTimeline={includeTimelineInJobDetailsTab}
              hideRouteHistoryAction
              mapActions={
                <SmallButton
                  color="inherit"
                  disableElevation
                  onClick={openWorkspace}
                  size="small"
                  startIcon={<Route />}
                  variant="outlined"
                  sx={{
                    border: `1px solid ${theme.brandV2.colors.treadGray7}`,
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}
                >
                  {t('approvals.approve_jobs.summary_hero.route_history_and_hours')}
                </SmallButton>
              }
            />
          </Box>
        </Box>
        {belowTimesheets && (
          <Box sx={{ gridColumn: 1, minWidth: 0 }}>{belowTimesheets}</Box>
        )}
      </Box>
    );
  },
);

export default JobDetailsRedesigned;
